import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Brain, Plus, Play, Loader2, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const QuizzesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteQuiz, setDeleteQuiz] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/quizzes`, {
        headers: getAuthHeaders(token)
      });
      setQuizzes(response.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteQuiz) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/quizzes/${deleteQuiz.quiz_id}`, {
        headers: getAuthHeaders(token)
      });
      toast.success("Quiz deleted successfully");
      setQuizzes(quizzes.filter(q => q.quiz_id !== deleteQuiz.quiz_id));
      setDeleteQuiz(null);
    } catch (error) {
      toast.error("Failed to delete quiz");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quizzes-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Quizzes
              </h1>
              <p className="text-slate-600">Take quizzes to learn and test your knowledge</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/books")}
            className="btn-accent gap-2"
            data-testid="create-quiz-btn"
          >
            <Plus className="w-4 h-4" />
            Create Quiz
          </Button>
        </div>

        {quizzes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quizzes.map((quiz) => (
              <Card 
                key={quiz.quiz_id}
                className="card-base card-interactive group"
                data-testid={`quiz-card-${quiz.quiz_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl flex items-center justify-center">
                      <Brain className="w-6 h-6 text-[#F97316]" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        {quiz.questions?.length || 0} questions
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteQuiz(quiz);
                        }}
                        data-testid={`delete-quiz-${quiz.quiz_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{quiz.title}</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Detail level: {quiz.config?.detail_level || 50}% • {new Date(quiz.created_at).toLocaleDateString()}
                  </p>

                  <Button
                    className="w-full btn-accent gap-2"
                    onClick={() => navigate(`/quiz/${quiz.quiz_id}/take`)}
                    data-testid={`start-quiz-${quiz.quiz_id}`}
                  >
                    <Play className="w-4 h-4" />
                    Start Quiz
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-base">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <Brain className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No quizzes yet</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Upload a book and generate your first learning quiz
              </p>
              <Button 
                onClick={() => navigate("/books")}
                className="btn-accent"
                data-testid="create-first-quiz-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Quiz
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteQuiz} onOpenChange={() => setDeleteQuiz(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quiz?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteQuiz?.title}"? This will also delete all quiz attempts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuizzesPage;
